/* eslint-disable max-len */
// #region types
/**
 * @typedef {Object} Address
 * @property {string} addressId UUID
 * @property {string} orgId UUID
 * @property {string} orgName
 * @property {string} line1
 * @property {string} line2
 * @property {string} line3
 * @property {string} town
 * @property {string} region
 * @property {string} postcode
 * @property {number} [countryId]
 * @property {string} country
 */

/**
 * @typedef {Object} Session
 * @property {string} name
 * @property {string} description
 * @property {string} endDateTime Date is already a formatted string
 * @property {string} startDateTime Date is already a formatted string
 * @property {Address} address
 * @property {boolean} limitNumberOfSpaces
 * @property {boolean} [mandatory]
 * @property {number} [maxSpaces]
 * @property {number} [spacesAvailable]
 * @property {number} [spacesBooked]
 */

/**
 * @typedef {Object} EventInstance
 * @property {string} id UUID
 * @property {string} eventId UUID
 * @property {string} [defaultSessionId] UUID
 * @property {number} [productId]
 * @property {boolean} showLocation
 * @property {boolean} showDisabilityOptions
 * @property {boolean} showDietaryRequirements
 * @property {string} location
 * @property {string} duration
 * @property {string} startDate Date is already a formatted string
 * @property {string} endDate Date is already a formatted string
 * @property {string} address
 * @property {string} postcode
 * @property {string} priceExVat
 * @property {number} [vatRate] double
 * @property {boolean} applyVat
 * @property {number} productId
 * @property {number} [spacesAvailable]
 * @property {number} [minDelegates]
 * @property {number} [maxDelegates]
 * @property {number} [maxDelegatesPerOrganisation]
 * @property {number} [maxDelegatesOnWaitingList]
 * @property {Array<Session>} sessions
 * @property {string} instanceType
 */
// #endregion types

/**
 * @type {Function} can't have more than the event max. null and zero or less means unlimited
 * @param {number} currentMax
 * @param {number} maxDelegates
 * @returns {number} the new max value
 */
const limitByMaxDelegates = ( currentMax, maxDelegates ) => ( maxDelegates > 0 && maxDelegates < currentMax ? maxDelegates : currentMax );

/**
 * @type {Function} can't have more than the organisation limit. ignores zero and -1 ( unlimited )
 * @param {number} currentMax
 * @param {number} maxDelegatesPerOrganisation
 * @returns {number} the new max value
 */
const limitByMaxDelegatesPerOrganisation = ( currentMax, maxDelegatesPerOrganisation ) => ( maxDelegatesPerOrganisation > 0 && maxDelegatesPerOrganisation < currentMax
  ? maxDelegatesPerOrganisation
  : currentMax );

/**
 * @type {Function} can't have more than the remaining spaces on the event
 * below zero means unlimited, we don't filter in that instance
 * @param {number} currentMax
 * @param {number} spacesAvailable
 * @returns {number} the new max value
 */
const limitBySpacesAvailable = ( currentMax, spacesAvailable ) => ( spacesAvailable !== null && spacesAvailable >= 0 && spacesAvailable < currentMax
  ? spacesAvailable
  : currentMax );

/**
 * @type {Function} waiting lists can have their own capacity
 * below zero means unlimited, we don't filter in that instance
 * @param {number} currentMax
 * @param {number} maxDelegatesOnWaitingList
 * @returns {number} the new max value
 */
const limitByMaxDelegatesOnWaitingList = ( currentMax, maxDelegatesOnWaitingList ) => ( maxDelegatesOnWaitingList > 0 && maxDelegatesOnWaitingList < currentMax
  ? maxDelegatesOnWaitingList
  : currentMax );

/**
 * @type {Function} calculates the maximum number of delegates the user
 * is allowed to attempt to add to a booking
 * @param {EventInstance} _ the event instance you want to calculate for
 * @param {number} [max] (default 50) the upper ceiling for spaces to return
 * @returns {number} a rough maximum number of spaces available
 */
export const calcBookingAvailability = (
  {
    maxDelegates, maxDelegatesPerOrganisation, spacesAvailable, sessions
  },
  max = 50
) => {
  max = limitByMaxDelegates( max, maxDelegates );
  max = limitByMaxDelegatesPerOrganisation( max, maxDelegatesPerOrganisation );
  max = limitBySpacesAvailable( max, spacesAvailable );

  // then we ensure that no mandatory sessions are below the current max value
  sessions.forEach( s => {
    // only counts if spaces are limited for a mandatory session
    // ignores everything if max is zero
    if ( max !== 0 && s.mandatory === true && s.limitNumberOfSpaces === true ) {
      max = limitByMaxDelegates( max, s.maxSpaces );

      // zero means it's run out of space
      max = limitBySpacesAvailable( max, s.spacesAvailable );
    }
  } );

  return max;
};

/**
 * @type {Function} calculates the maximum number of delegates the user
 * is allowed to attempt to add to a wishlist
 * @param {EventInstance} _ the event instance you want to calculate for
 * @param {number} [max] (default 50) the upper ceiling for spaces to return
 * @returns {number} a rough maximum number of spaces available
 */
export const calcWishlistAvailability = ( { maxDelegates }, max = 50 ) => {
  max = limitByMaxDelegates( max, maxDelegates );
  return max;
};

/**
 * @type {Function} calculates the maximum number of delegates the user
 * is allowed to attempt to add to the waiting list
 * @param {EventInstance} _ the event instance you want to calculate for
 * @param {number} [max] (default 50) the upper ceiling for spaces to return
 * @returns {number} a rough maximum number of spaces available
 */
export const calcWaitingListAvailability = ( { maxDelegatesOnWaitingList }, max = 50 ) => {
  max = limitByMaxDelegatesOnWaitingList( max, maxDelegatesOnWaitingList );
  return max;
};

/**
 * @type {Function} calculates the remaining availability on a booking as a percentage.
 * @param {EventInstance} _ the event instance you want to calculate for
 * @param {number} [precision] (default 2) the precision of the returned value
 * @returns {number} A percentage of the available the spaces or NaN
 */
export const calcAvailabilityPercentage = (
  { maxDelegates, spacesAvailable },
  precision = 2
) => Math.round( ( spacesAvailable / maxDelegates ) * 100 ).toFixed( precision );

export default {
  calcBookingAvailability,
  calcWaitingListAvailability,
  calcWishlistAvailability,
  calcAvailabilityPercentage
};

// #region debug
// const instance = {
//   spacesAvailable: 100,
//   minDelegates: null,
//   maxDelegates: 100,
//   maxDelegatesPerOrganisation: 30,
//   maxDelegatesOnWaitingList: 30,
//   sessions: [{
//     mandatory: true,
//     limitNumberOfSpaces: true,
//     maxSpaces: 100,
//     spacesAvailable: 4,
//     spacesBooked: 100
//   }]
// };

// console.log( "booking:", calcBookingAvailability( instance ) );
// console.log( "wishing:", calcWishlistAvailability( instance ) );
// console.log( "waiting:", calcWaitingListAvailability( instance ) );
// #endregion debug
